import {
  EditParentTemplateFolder,
  GetParentTemplateFolder,
  RegisterParentTemplateFolder,
} from "@/dtos/ParentTemplateFolder.dto";
import { StateObject } from "@/dtos/State.dto";
import ParentTemplateFolder from "@/services/template/parent-template-folder";
import {
  CLEAR_PARENT_TEMPLATE_FOLDER_ISLOADING,
  DELETE_PARENT_TEMPLATE_FOLDER,
  EDIT_PARENT_TEMPLATE_FOLDER,
  GET_PARENT_TEMPLATE_FOLDERS,
  REGISTER_PARENT_TEMPLATE_FOLDER,
} from "@/store/action-types";
import {
  SET_ERRORMSG,
  SET_PARENT_TEMPLATE_FOLDER,
  SET_PARENT_TEMPLATE_FOLDERS,
} from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const parentTemplateFolder: Module<any, {}> = {
  state: {
    parentTemplateFolder: {
      data: null,
      isLoading: false,
    },
    parentTemplateFolders: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    parentTemplateFolder: (state: { parentTemplateFolder: any }) => state.parentTemplateFolder,
    parentTemplateFolders: (state: { parentTemplateFolders: any }) => state.parentTemplateFolders,
  },
  mutations: {
    [SET_PARENT_TEMPLATE_FOLDER]: (
      state: { parentTemplateFolder: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.parentTemplateFolder.data = payload.data;
      state.parentTemplateFolder.isLoading = payload.isLoading;
    },
    [SET_PARENT_TEMPLATE_FOLDERS]: (
      state: { parentTemplateFolders: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.parentTemplateFolders.data = payload.data;
      state.parentTemplateFolders.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_PARENT_TEMPLATE_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await ParentTemplateFolder.registerParentTemplateFolder(
          payload as RegisterParentTemplateFolder
        );

        if (result) {
          if (result.data.status) {
            commit(SET_PARENT_TEMPLATE_FOLDER, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_PARENT_TEMPLATE_FOLDER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: false });
        commit(
          SET_ERRORMSG,
          error.response?.data?.message || "Failed to register template folder."
        );
        return false;
      }
    },
    [GET_PARENT_TEMPLATE_FOLDERS]: async ({ commit }: any, payload: any) => {
      commit(SET_PARENT_TEMPLATE_FOLDERS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await ParentTemplateFolder.getParentTemplateFolders(
          payload as GetParentTemplateFolder
        );

        if (result) {
          if (result.data.status) {
            commit(SET_PARENT_TEMPLATE_FOLDERS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_PARENT_TEMPLATE_FOLDERS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_PARENT_TEMPLATE_FOLDERS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_PARENT_TEMPLATE_FOLDERS, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again.");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error. Please try again.");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Template folders not found.");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network error. Please check your connection.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load template folders.");
        }

        return Promise.resolve(false);
      }
    },
    [EDIT_PARENT_TEMPLATE_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await ParentTemplateFolder.editParentTemplateFolder(
          payload as EditParentTemplateFolder
        );

        if (result) {
          if (result.data.status) {
            commit(SET_PARENT_TEMPLATE_FOLDER, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_PARENT_TEMPLATE_FOLDER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: false });
        commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit template folder.");
        return false;
      }
    },
    [DELETE_PARENT_TEMPLATE_FOLDER]: async ({ commit }: any, payload: string) => {
      commit(SET_PARENT_TEMPLATE_FOLDER, {
        data: { id: payload },
        isLoading: true,
      });

      try {
        const result: AxiosResponse =
          await ParentTemplateFolder.deleteParentTemplateFolder(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_PARENT_TEMPLATE_FOLDER, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_PARENT_TEMPLATE_FOLDER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_PARENT_TEMPLATE_FOLDER, { data: null, isLoading: false });
        commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete template folder.");
        return false;
      }
    },
    [CLEAR_PARENT_TEMPLATE_FOLDER_ISLOADING]: ({ commit }) => {
      commit(SET_PARENT_TEMPLATE_FOLDER, { isLoading: false });
      commit(SET_PARENT_TEMPLATE_FOLDERS, { isLoading: false });
    },
  },
};

export default parentTemplateFolder;
